import React, { useEffect, useState } from "react";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";
import { Badge } from "../../../Component/AdminComponents/Badge";
import { format } from "date-fns";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { Eye, Pencil, Plus, Power, ShieldCheck, Upload } from "lucide-react";
import BlueButton from "../../../Component/Common/BlueButton";
import BrandModal from "./BrandModal";
import { BASE_URL } from "../../../config/api";
import SimpleTable from "../../../Component/Common/SimpleTable";
import AddButton from "../../../Component/Common/AddButton";
import SearchInput from "../../../Component/Common/SearchInput";
import UpdateButton from "../../../Component/Common/UpdateButton";
import StatusButton from "../../../Component/Common/StatusButton";
import { useNavigate } from "react-router-dom";
import axios from "axios";
// import BrandModal from "./BrandModal"; // If modal exists

export default function BrandView() {
  const [brands, setBrands] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const navigate = useNavigate();


  // ----------------- FETCH API -----------------
  const fetchBrands = async () => {

    try {

      setLoading(
        true
      );

      setError(
        ""
      );

      const response =
        await axios.get(
          `${BASE_URL}warranty/get-brands`,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const data =
        response.data;

      setBrands(
        data.data
      );

      setFiltered(
        data.data
      );

    } catch (err) {

      setError(
        err?.response?.data
          ?.message ||
        err?.message ||
        "Something went wrong"
      );

    } finally {

      setLoading(
        false
      );

    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  // ----------------- SEARCH FILTER -----------------
  useEffect(() => {
    const result = brands.filter((b) =>
      b.name.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(result);
  }, [search, brands]);

  // ----------------- HANDLE SUBMIT -----------------
  const handleSubmit = () => {
    setIsOpen(false);
    setSelectedBrand(null);
    fetchBrands();
  };

  // ----------------- HANDLE ADD / UPDATE -----------------
  const handleAdd = () => {
    setSelectedBrand(null);
    setIsOpen(true);
  };

  const handleUpdate = (brand) => {
    setSelectedBrand(brand);
    setIsOpen(true);
  };

  // ----------------- TOGGLE STATUS -----------------
  const toggleStatus = async (
  brand
) => {

  try {

    await axios.post(
      `${BASE_URL}warranty/brand/toggle-status/${brand.id}`,
      {},
      {
        headers: {
          Accept:
            "application/json",

          "Content-Type":
            "application/json",
        },
      }
    );

    fetchBrands();

  } catch (err) {

    console.error(
      err
    );

    alert(
      err?.response?.data
        ?.message ||
      err?.message ||
      "Something went wrong"
    );

  }
};

  const brandRows = filtered.map((brand) => {
  const isActive = brand.status === 1;

  return {
    raw: brand, // 🔥 full object for actions

    cells: [
      // Image
      {
        title: (
          <div className="flex justify-center">
            <img
              src={brand.image || "/placeholder.png"}
              alt={brand.name}
              className="h-10 w-10 rounded-lg object-cover border"
            />
          </div>
        ),
      },

      // Brand Name
      {
        title: brand.name,
      },

      // Categories
      {
        title:
          brand.categories && brand.categories.length > 0 ? (
            <div className="flex flex-wrap justify-center gap-1">
              {brand.categories.map((cat) => (
                <span
                  key={cat.id}
                  className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 text-[10px]"
                >
                  {cat.name}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-slate-400">—</span>
          ),
      },

      // Status badge
      {
        title: (
          <Badge color={isActive ? "green" : "red"}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
        ),
      },

      // Created
      {
        title: brand.created_at
          ? format(new Date(brand.created_at), "dd MMM yyyy")
          : "—",
      },

      // Updated
      {
        title: brand.updated_at
          ? format(new Date(brand.updated_at), "dd MMM yyyy")
          : "—",
      },
    ],
  };
});


  return (
    <div className="space-y-4">
      <SectionCard title="Warranty Brands">
        {/* RIGHT SIDE BUTTONS */}
       <div className="flex justify-end mb-3 gap-2">
         <SearchInput
            value={search}
            onChange={setSearch}
            onClear={() => setSearch("")}
            placeholder="Search brand..."
            size="sm"
          />

          <AddButton onClick={handleAdd} />
          <div className="hidden lg:block">
            <RefreshButton onClick={fetchBrands} />
          </div>
        </div>

        {/* MODAL (uncomment when ready) */}
        <BrandModal
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setSelectedBrand(null);
          }}
          onSubmit={handleSubmit}
          initialData={selectedBrand}
        />

        {/* LOADER */}
        {loading && <RegularLoader />}

        {/* ERROR */}
        {error && (
          <div className="text-center py-4 text-red-500 text-sm">{error}</div>
        )}

        {/* TABLE */}
        {!loading && !error && (
        <>

          {/* Mobile Card Layout - Profile UI with 3 Dot Menu */}
          <div className="md:hidden space-y-2">
            {filtered.map((brand, index) => {
              const isActive = brand.status === 1;
              const isOpen = openMenuId === brand.id;

              return (
                <div
                  key={brand.id}
                  className="relative bg-white rounded-2xl border border-slate-300 shadow-sm hover:shadow-md transition-all duration-200 px-3 py-3"
                >
                  {/* TOP SECTION */}
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      {/* Image */}
                      <div className="relative">
                        <img
                          src={brand.image || "/placeholder.png"}
                          alt={brand.name}
                          className="h-14 w-14 rounded-xl object-cover border border-slate-200"
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        {/* Name + Status */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-semibold text-slate-900">
                            {brand.name}
                          </h3>

                          <Badge
                            color={isActive ? "green" : "red"}
                          >
                            {isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        {/* Categories */}
                        <div className="mt-2 flex flex-wrap gap-1">
                          {brand.categories?.length > 0 ? (
                            brand.categories.map((cat) => (
                              <span
                                key={cat.id}
                                className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium border border-blue-100"
                              >
                                {cat.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-slate-400">
                              No categories
                            </span>
                          )}
                        </div>

                        {/* Dates */}
                        {/* <div className="mt-3 space-y-1 text-xs">
                          <div className="flex gap-1">
                            <span className="text-slate-400 font-medium">
                              Created:
                            </span>
                            <span className="text-slate-700 font-medium">
                              {brand.created_at
                                ? format(new Date(brand.created_at), "dd MMM yyyy")
                                : "—"}
                            </span>
                          </div>

                          <div className="flex gap-1">
                            <span className="text-slate-400 font-medium">
                              Updated:
                            </span>
                            <span className="text-slate-700 font-medium">
                              {brand.updated_at
                                ? format(new Date(brand.updated_at), "dd MMM yyyy")
                                : "—"}
                            </span>
                          </div>
                        </div> */}
                      </div>
                    </div>

                    {/* 3 DOT MENU */}
                    <button
                      onClick={() =>
                        setOpenMenuId(isOpen ? null : brand.id)
                      }
                      className="text-xl text-slate-400 hover:text-slate-600 transition"
                    >
                      ⋮
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="mt-4 border-t border-slate-100" />

                  {/* Optional Footer Info */}
                  {/* <div className="mt-3 text-[11px] text-slate-400">
                    Brand ID: {brand.id}
                  </div> */}

                  {/* DROPDOWN */}
                  {isOpen && (
                    <div className="absolute top-10 right-3 z-20 w-44 bg-white rounded-2xl shadow-[0_12px_30px_rgba(0,0,0,0.12)] border border-slate-200 overflow-hidden">

                      {/* View */}
                      <button
                        onClick={() => {
                          navigate(`/admin/brand/${brand.id}/models`);
                          setOpenMenuId(null);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 transition"
                      >
                        <Eye size={16} className="text-blue-600" />
                        View
                      </button>

                      <div className="border-t border-slate-100" />

                      {/* Update */}
                      <button
                        onClick={() => {
                          handleUpdate(brand);
                          setOpenMenuId(null);
                        }}
                        className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 transition"
                      >
                        <Pencil size={16} className="text-blue-600" />
                        Update
                      </button>

                      <div className="border-t border-slate-100" />

                      {/* Activate / Deactivate */}
                      <button
                        onClick={() => {
                          toggleStatus(brand);
                          setOpenMenuId(null);
                        }}
                        className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition
                          ${
                            isActive
                              ? "text-rose-600 hover:bg-rose-50"
                              : "text-emerald-600 hover:bg-emerald-50"
                          }`}
                      >
                        <Power
                          size={16}
                          className={isActive ? "text-rose-600" : "text-emerald-600"}
                        />
                        {isActive ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden md:block">
          <SimpleTable
            headers={[
              "Image",
              "Brand Name",
              "Categories",
              "Status",
              "Created",
              "Updated",
            ]}
            rows={brandRows}
            renderActions={(brand) => {
              const isActive = brand.status === 1;
              const buttonLabel = isActive ? "Inactive" : "Active";

              return (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => navigate(`/admin/brand/${brand.id}/models`)}
                    className="px-3 py-1 text-xs font-semibold rounded-xl bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                  >
                    View
                  </button>

                  <UpdateButton onClick={() => handleUpdate(brand)} />

                  <StatusButton
                    label={buttonLabel}
                    onClick={() => toggleStatus(brand)}
                  />
                </div>

              );
            }}
            showSrNo
            pageSize={10}
          />
          </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}



// import React, { useEffect, useState } from "react";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { format } from "date-fns";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { Plus, ShieldCheck, Upload } from "lucide-react";
// import BlueButton from "../../../Component/Common/BlueButton";
// import BrandModal from "./BrandModal";
// import { BASE_URL } from "../../../config/api";
// import SimpleTable from "../../../Component/Common/SimpleTable";
// import AddButton from "../../../Component/Common/AddButton";
// import SearchInput from "../../../Component/Common/SearchInput";
// import UpdateButton from "../../../Component/Common/UpdateButton";
// import StatusButton from "../../../Component/Common/StatusButton";
// // import BrandModal from "./BrandModal"; // If modal exists

// export default function BrandView() {
//   const [brands, setBrands] = useState([]);
//   const [filtered, setFiltered] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [search, setSearch] = useState("");
//   const [isOpen, setIsOpen] = useState(false);
//   const [selectedBrand, setSelectedBrand] = useState(null);

//   // ----------------- FETCH API -----------------
//   const fetchBrands = async () => {
//     try {
//       setLoading(true);
//       setError("");

//       const res = await fetch(`${BASE_URL}warranty/get-brands`);
//       if (!res.ok) throw new Error("Failed to fetch brands");

//       const data = await res.json();
//       setBrands(data.data);
//       setFiltered(data.data);
//     } catch (err) {
//       setError(err.message || "Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBrands();
//   }, []);

//   // ----------------- SEARCH FILTER -----------------
//   useEffect(() => {
//     const result = brands.filter((b) =>
//       b.name.toLowerCase().includes(search.toLowerCase())
//     );
//     setFiltered(result);
//   }, [search, brands]);

//   // ----------------- HANDLE SUBMIT -----------------
//   const handleSubmit = () => {
//     setIsOpen(false);
//     setSelectedBrand(null);
//     fetchBrands();
//   };

//   // ----------------- HANDLE ADD / UPDATE -----------------
//   const handleAdd = () => {
//     setSelectedBrand(null);
//     setIsOpen(true);
//   };

//   const handleUpdate = (brand) => {
//     setSelectedBrand(brand);
//     setIsOpen(true);
//   };

//   // ----------------- TOGGLE STATUS -----------------
//   const toggleStatus = async (brand) => {
//     try {
//       const res = await fetch(
//        `${BASE_URL}warranty/brand/toggle-status/${brand.id}`,
//         { method: "POST" }
//       );

//       if (!res.ok) throw new Error("Failed to toggle status");
//       fetchBrands();
//     } catch (err) {
//       alert(err.message || "Something went wrong");
//     }
//   };

//   const brandRows = filtered.map((brand) => {
//   const isActive = brand.status === 1;

//   return {
//     raw: brand, // 🔥 full object for actions

//     cells: [
//       // Image
//       {
//         title: (
//           <div className="flex justify-center">
//             <img
//               src={brand.image || "/placeholder.png"}
//               alt={brand.name}
//               className="h-10 w-10 rounded-lg object-cover border"
//             />
//           </div>
//         ),
//       },

//       // Brand Name
//       {
//         title: brand.name,
//       },

//       // Categories
//       {
//         title:
//           brand.categories && brand.categories.length > 0 ? (
//             <div className="flex flex-wrap justify-center gap-1">
//               {brand.categories.map((cat) => (
//                 <span
//                   key={cat.id}
//                   className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 text-[10px]"
//                 >
//                   {cat.name}
//                 </span>
//               ))}
//             </div>
//           ) : (
//             <span className="text-slate-400">—</span>
//           ),
//       },

//       // Status badge
//       {
//         title: (
//           <Badge color={isActive ? "green" : "red"}>
//             {isActive ? "Active" : "Inactive"}
//           </Badge>
//         ),
//       },

//       // Created
//       {
//         title: brand.created_at
//           ? format(new Date(brand.created_at), "dd MMM yyyy")
//           : "—",
//       },

//       // Updated
//       {
//         title: brand.updated_at
//           ? format(new Date(brand.updated_at), "dd MMM yyyy")
//           : "—",
//       },
//     ],
//   };
// });


//   return (
//     <div className="space-y-4">
//       <SectionCard title="Warranty Brands">
//         {/* RIGHT SIDE BUTTONS */}
//        <div className="flex flex-wrap justify-end mb-3 gap-2">
//          <SearchInput
//             value={search}
//             onChange={setSearch}
//             onClear={() => setSearch("")}
//             placeholder="Search brand..."
//             size="sm"
//           />

//           <AddButton onClick={handleAdd} />
//           <RefreshButton onClick={fetchBrands} />
//         </div>

//         {/* MODAL (uncomment when ready) */}
//         <BrandModal
//           isOpen={isOpen}
//           onClose={() => {
//             setIsOpen(false);
//             setSelectedBrand(null);
//           }}
//           onSubmit={handleSubmit}
//           initialData={selectedBrand}
//         />

//         {/* LOADER */}
//         {loading && <RegularLoader />}

//         {/* ERROR */}
//         {error && (
//           <div className="text-center py-4 text-red-500 text-sm">{error}</div>
//         )}

//         {/* TABLE */}
//         {!loading && !error && (
//           <SimpleTable
//             headers={[
//               "Image",
//               "Brand Name",
//               "Categories",
//               "Status",
//               "Created",
//               "Updated",
//             ]}
//             rows={brandRows}
//             renderActions={(brand) => {
//               const isActive = brand.status === 1;
//               const buttonLabel = isActive ? "Inactive" : "Active";

//               return (
//                 <div className="flex items-center justify-center gap-2">
//                   <UpdateButton onClick={() => handleUpdate(brand)} />

//                   <StatusButton
//                     label={buttonLabel}
//                     onClick={() => toggleStatus(brand)}
//                   />
//                 </div>
//               );
//             }}
//             showSrNo
//             pageSize={10}
//           />
//         )}
//       </SectionCard>
//     </div>
//   );
// }
